import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  createFilterOptions,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon,
  PhoneAndroid as PhoneAndroidIcon,
  Computer as ComputerIcon,
} from '@mui/icons-material';
import auditLogService from '../../services/auditLogService';
import { formatServerDateTime } from '../../utils/dateUtils';
import { AuditLog, AuditLogQuery } from '../../types/auditLog';
import { userService } from '../../services/userService';
import { UserListItem } from '../../types/user';
import { auditEntityTypeLabel } from './auditEntityTypes';
import { apiErrorMessage } from '../../utils/apiErrors';

/** A „Végrehajtó" lista betöltési hibájának tartaléka (ha a szerver nem indokolt). */
export const ACTORS_LOAD_FALLBACK = 'Nem sikerült betölteni a felhasználókat.';
/** …és a teendő: a lenyíló bezárása + újranyitása újrapróbál (`loadActorOptions`). */
export const ACTORS_RETRY_HINT = 'Zárd be, és nyisd meg újra a listát az újrapróbáláshoz.';

/**
 * A „Végrehajtó" választó szűrője [Web UX 3. csomag (C), 2026-10-04]: a lokális DB-ben 3300+
 * felhasználó van (mérve), egy szűretlen lista megnyitáskor mindet kirajzolná. Legfeljebb 50 találat,
 * kis- és ékezet-érzéketlenül (a MUI alapértelmezése), a névre ÉS az e-mailre.
 */
const actorFilterOptions = createFilterOptions<UserListItem>({
  limit: 50,
  stringify: (u) => `${u.name} ${u.email}`,
});

const AuditLogViewer: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Filters
  const [entityTypes, setEntityTypes] = useState<string[]>([]);
  const [selectedEntityType, setSelectedEntityType] = useState('');
  const [selectedAction, setSelectedAction] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedClientSource, setSelectedClientSource] = useState<'' | 'mobile' | 'web'>('');
  /**
   * „VÉGREHAJTÓ" SZŰRŐ [Web UX 3. csomag (C), 2026-10-04 — WEB_UX 1.6]: ki végezte a műveletet
   * (`AuditLog.UserId`). ⚠️ A jelentés szerint ehhez backend-paraméter kellett volna — NEM IGAZ: a
   * `AuditLogQueryDto.UserId` (`backend/PinyoApi/DTOs/AuditLogDto.cs:25`) már létezett, és a
   * kontroller alkalmazza (`AuditLogController.cs:57-60`). Ugyanaz a minta, mint a többi szűrőnél:
   * kiválasztás → „Szűrés" (1. oldal), „Törlés" → üres.
   * A felhasználó-lista LUSTÁN, a választó első megnyitásakor töltődik (nem a lap betöltésekor):
   * így a lap mount-kori kérései nem nőnek (lásd a `AuditLogViewer.test.tsx` hívásszám-őreit).
   * A „mi történt Kiss Dániellel" (TÁRGY szerinti) szűrő SZÁNDÉKOSAN nincs: a jelenlét-sorok
   * `EntityId`-je nem a felhasználó azonosítója, egy `EntityId`-szűrő félrevezetően keveset mutatna.
   */
  const [selectedActor, setSelectedActor] = useState<UserListItem | null>(null);
  const [actorOptions, setActorOptions] = useState<UserListItem[]>([]);
  const [actorsLoading, setActorsLoading] = useState(false);
  /**
   * A felhasználó-lista betöltési HIBÁJA [Web UX 3. csomag (C), 1. validátor-kör F1, 2026-10-04].
   * Korábban a hiba csak a konzolba ment, és a lenyíló „Nincs ilyen felhasználó"-t írt — VALÓTLANUL
   * (nem az volt, hogy nincs ilyen, hanem hogy a lista nem jött le). Most a lenyíló a hibát mondja
   * (a szerver magyar indoklása / a hálózati mondat, `apiErrorMessage`), és a következő megnyitás
   * újrapróbál (`actorsRequestedRef` visszaáll).
   */
  const [actorsError, setActorsError] = useState('');
  const actorsRequestedRef = useRef(false);

  // Expanded rows
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  // Detail dialog
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  // Mount-only betöltés: az entitástípus-lista gyakorlatilag statikus, és az első oldal is csak
  // egyszer kell. Minden további lekérést (lapozás, szűrés) EXPLICIT paraméterekkel indítunk innen
  // lejjebb, nem side-effectre/setTimeout-ra támaszkodva — így mountkor és szűrő-váltásnál is
  // pontosan egyszer fut le a hálózati hívás, nem duplán. [Web UX #10, 2026-09-12]
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadEntityTypes();
    loadAuditLogs({});
  }, []);

  const loadEntityTypes = async () => {
    try {
      const types = await auditLogService.getEntityTypes();
      setEntityTypes(types);
    } catch (err: any) {
      console.error('Failed to load entity types:', err);
    }
  };

  const loadActorOptions = async () => {
    if (actorsRequestedRef.current) return;
    actorsRequestedRef.current = true;
    setActorsLoading(true);
    setActorsError('');
    try {
      const result = await userService.getUsers();
      // Az inaktív (törölt) felhasználó is maradjon: a régi naplósorok végrehajtója lehet.
      const sorted = [...(result.data || [])].sort((a, b) => a.name.localeCompare(b.name, 'hu'));
      setActorOptions(sorted);
    } catch (err: any) {
      actorsRequestedRef.current = false; // a következő megnyitás újrapróbálja
      setActorsError(apiErrorMessage(err, ACTORS_LOAD_FALLBACK));
      console.error('Failed to load users for the actor filter:', err);
    } finally {
      setActorsLoading(false);
    }
  };

  type AuditLogFilterOverrides = {
    page?: number;
    rowsPerPage?: number;
    entityType?: string;
    action?: string;
    startDate?: string;
    endDate?: string;
    clientSource?: '' | 'mobile' | 'web';
    userId?: string;
  };

  /**
   * Mindig explicit paraméterekkel hívjuk (page/rowsPerPage/szűrők), mert a hívó helyeken a
   * frissen beállított állapot (pl. setPage(0)) még nem érkezett meg a state-be, amikor a
   * lekérést el kellene indítani — a state-re hagyatkozás okozta a duplikált hívást korábban.
   */
  const loadAuditLogs = async (overrides: AuditLogFilterOverrides) => {
    try {
      setLoading(true);
      setError('');

      const targetPage = overrides.page ?? page;
      const targetRowsPerPage = overrides.rowsPerPage ?? rowsPerPage;
      const targetEntityType = overrides.entityType ?? selectedEntityType;
      const targetAction = overrides.action ?? selectedAction;
      const targetStartDate = overrides.startDate ?? startDate;
      const targetEndDate = overrides.endDate ?? endDate;
      const targetClientSource = overrides.clientSource ?? selectedClientSource;
      const targetUserId = overrides.userId ?? selectedActor?.userId ?? '';

      const query: AuditLogQuery = {
        page: targetPage + 1, // Backend uses 1-based pagination
        pageSize: targetRowsPerPage,
      };

      if (targetEntityType) query.entityType = targetEntityType;
      if (targetAction) query.action = targetAction;
      if (targetStartDate) query.startDate = targetStartDate;
      if (targetEndDate) query.endDate = targetEndDate;
      if (targetClientSource) query.clientSource = targetClientSource;
      if (targetUserId) query.userId = targetUserId;

      const response = await auditLogService.getAuditLogs(query);
      setLogs(response.logs);
      setTotalCount(response.totalCount);
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Hiba történt az audit logok betöltésekor')); // [Web UX 3. csomag (D), 2026-10-04]
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
    loadAuditLogs({ page: newPage });
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    loadAuditLogs({ page: 0, rowsPerPage: newRowsPerPage });
  };

  const handleFilterChange = () => {
    setPage(0);
    loadAuditLogs({ page: 0 });
  };

  const handleResetFilters = () => {
    setSelectedEntityType('');
    setSelectedAction('');
    setStartDate('');
    setEndDate('');
    setSelectedClientSource('');
    setSelectedActor(null);
    setPage(0);
    loadAuditLogs({ page: 0, entityType: '', action: '', startDate: '', endDate: '', clientSource: '', userId: '' });
  };

  // Backend sends UTC time („Z” nélkül). [Web UX 1. csomag (4.3), 2026-10-03] a korábbi saját „Z”-fűzés +
  // `timeZone: 'Europe/Budapest'` helyett a közös segéd — a kimenet bitre azonos
  // (`2026. 10. 02. 15:15:30`), de egy `+02:00` végű szövegből sem csinál érvénytelen dátumot.
  const formatDate = (dateString: string) => formatServerDateTime(dateString, { withSeconds: true, fallback: dateString ?? '' });

  // A backend controllerei nem csak CREATE/UPDATE/DELETE-et írnak — grep `LogAsync("` /
  // `LogCreateAsync` / `LogUpdateAsync` / `LogDeleteAsync` a Controllers alatt (NB-1 audit).
  // Az érték egyszerre szolgál (1) a "Művelet" szűrő legördülő opcióihoz Magyar címkével és
  // (2) a Chip-színezéshez kategóriánként (siker-zöld / info-kék / hiba-piros / figyelmeztetés-sárga).
  const ACTION_OPTIONS: { value: string; label: string; color: 'success' | 'info' | 'error' | 'warning' }[] = [
    { value: 'CREATE', label: 'Létrehozás', color: 'success' },
    { value: 'UPDATE', label: 'Módosítás', color: 'info' },
    { value: 'DELETE', label: 'Törlés', color: 'error' },
    { value: 'LOGIN', label: 'Bejelentkezés', color: 'info' },
    { value: 'REGISTER', label: 'Jelentkezés', color: 'success' },
    { value: 'RE-REGISTER', label: 'Újra-jelentkezés', color: 'success' },
    { value: 'UNREGISTER', label: 'Jelentkezés visszavonása', color: 'error' },
    { value: 'SUBSTITUTION_REQUEST', label: 'Helyettesítés kérése', color: 'info' },
    { value: 'COACH_REASSIGNED', label: 'Edző átrendelése', color: 'info' },
    { value: 'UPDATE_ATTENDANCE', label: 'Jelenlét módosítása', color: 'info' },
    { value: 'ATTENDANCE_BULK_UPDATE', label: 'Jelenléti ív mentése', color: 'info' },
    { value: 'UPDATE_PAYMENTS', label: 'Fizetés módosítása', color: 'info' },
    { value: 'UPDATE_OPERATIONAL_DATA', label: 'Operatív adatok módosítása', color: 'info' },
    { value: 'APPROVE', label: 'Jóváhagyás', color: 'success' },
    { value: 'REJECT', label: 'Elutasítás', color: 'error' },
    { value: 'COACH_AVAILABILITY_SERIES_CREATE', label: 'Elérhetőség-sorozat létrehozása', color: 'success' },
    { value: 'COACH_AVAILABILITY_SERIES_DELETE', label: 'Elérhetőség-sorozat törlése', color: 'error' },
    { value: 'MESSAGE_DIRECT_SEND', label: 'Egyéni üzenet küldése', color: 'success' },
    { value: 'EMAIL_BULK_SEND', label: 'Tömeges e-mail küldése', color: 'success' },
    { value: 'PRIVATE_LESSON_REQUEST_STATUS', label: 'Magánóra-igénylés státusza', color: 'info' },
    { value: 'ACCOUNT_DELETION_REQUESTED', label: 'Fiók törlésének kérése', color: 'error' },
    { value: 'USER_DATA_ERASED', label: 'Felhasználói adatok törlése', color: 'error' },
    { value: 'DATA_RETENTION_PURGE', label: 'Lejárt naplók törlése', color: 'error' },
    { value: 'CLIENT_ERROR', label: 'Kliens hiba', color: 'warning' },
  ];

  const getActionColor = (action: string): 'success' | 'info' | 'error' | 'warning' | 'default' => {
    const upper = action.toUpperCase();
    const known = ACTION_OPTIONS.find((opt) => opt.value === upper);
    if (known) return known.color;

    // Biztonsági háló ismeretlen/jövőbeli action-nevekhez, hogy azok se kapjanak értelmetlen szürkét.
    if (upper.includes('DELETE') || upper.includes('REJECT') || upper.includes('ERASE') || upper.includes('PURGE') || upper.includes('UNREGISTER')) {
      return 'error';
    }
    if (upper.includes('CREATE') || upper.includes('APPROVE') || upper.includes('SEND') || upper.includes('REGISTER')) {
      return 'success';
    }
    if (upper.includes('ERROR')) return 'warning';
    if (upper.includes('UPDATE') || upper.includes('LOGIN') || upper.includes('REQUEST') || upper.includes('STATUS')) {
      return 'info';
    }
    return 'default';
  };

  const handleRowClick = (logId: string) => {
    setExpandedRow(expandedRow === logId ? null : logId);
  };

  const handleShowDetails = (log: AuditLog) => {
    setSelectedLog(log);
    setDetailDialogOpen(true);
  };

  const getSourceLabel = (clientSource: AuditLog['clientSource']): string => {
    if (clientSource === 'mobile') return 'Mobil';
    if (clientSource === 'web') return 'Web';
    return '—';
  };

  const renderSourceChip = (clientSource: AuditLog['clientSource']) => {
    if (clientSource === 'mobile') {
      return <Chip label="Mobil" color="secondary" size="small" icon={<PhoneAndroidIcon />} />;
    }
    if (clientSource === 'web') {
      return <Chip label="Web" color="default" size="small" icon={<ComputerIcon />} />;
    }
    return <Typography color="text.secondary">—</Typography>;
  };

  const safeParseJSON = (jsonString: string | null | undefined): any => {
    if (!jsonString) return null;
    try {
      return JSON.parse(jsonString);
    } catch (error) {
      console.error('Failed to parse JSON:', error);
      return jsonString; // Return original string if parsing fails
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Audit Napló
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            {/* [Web UX 3. csomag (C), 2026-10-04] magyar típusnév, ismeretlennél a nyers — `auditEntityTypes.ts`. */}
            <InputLabel>Típus</InputLabel>
            <Select
              data-testid="audit-entity-type-filter"
              value={selectedEntityType}
              label="Típus"
              onChange={(e) => setSelectedEntityType(e.target.value)}
            >
              <MenuItem value="">
                <em>Minden</em>
              </MenuItem>
              {[...entityTypes]
                .sort((a, b) => auditEntityTypeLabel(a).localeCompare(auditEntityTypeLabel(b), 'hu'))
                .map((type) => (
                  <MenuItem key={type} value={type}>
                    {auditEntityTypeLabel(type)}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Művelet</InputLabel>
            <Select
              data-testid="audit-action-filter"
              value={selectedAction}
              label="Művelet"
              onChange={(e) => setSelectedAction(e.target.value)}
            >
              <MenuItem value="">
                <em>Minden</em>
              </MenuItem>
              {ACTION_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Forrás</InputLabel>
            <Select
              data-testid="audit-source-filter"
              value={selectedClientSource}
              label="Forrás"
              onChange={(e) => setSelectedClientSource(e.target.value as '' | 'mobile' | 'web')}
            >
              <MenuItem value="">
                <em>Mind</em>
              </MenuItem>
              <MenuItem value="web">Web</MenuItem>
              <MenuItem value="mobile">Mobil</MenuItem>
            </Select>
          </FormControl>

          <Autocomplete
            size="small"
            sx={{ minWidth: 220 }}
            data-testid="audit-user-filter"
            options={actorOptions}
            value={selectedActor}
            onChange={(_, value) => setSelectedActor(value)}
            onOpen={loadActorOptions}
            loading={actorsLoading}
            filterOptions={actorFilterOptions}
            getOptionLabel={(u) => u.name}
            isOptionEqualToValue={(a, b) => a.userId === b.userId}
            renderOption={(props, u) => {
              const { key, ...rest } = props as any;
              return (
                <li key={u.userId} {...rest}>
                  <Box>
                    <Typography variant="body2">{u.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                  </Box>
                </li>
              );
            }}
            loadingText="Betöltés…"
            noOptionsText={
              actorsError ? (
                <Box component="span" role="alert" data-testid="audit-user-filter-error" sx={{ color: 'error.main' }}>
                  {actorsError} {ACTORS_RETRY_HINT}
                </Box>
              ) : (
                'Nincs ilyen felhasználó'
              )
            }
            renderInput={(params) => <TextField {...params} label="Végrehajtó" placeholder="Név vagy e-mail" />}
          />

          <TextField
            size="small"
            type="date"
            label="Kezdő dátum"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            size="small"
            type="date"
            label="Befejező dátum"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <Button variant="contained" onClick={handleFilterChange} disabled={loading}>
            Szűrés
          </Button>

          <Button variant="outlined" onClick={handleResetFilters} disabled={loading}>
            Törlés
          </Button>

          {/* A frissítés-gomb a MouseEvent-et NEM adhatja át overrides-ként (típushiba, a CRA build elbukna). */}
          <IconButton onClick={() => loadAuditLogs({})} disabled={loading} aria-label="Frissítés">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="40px"></TableCell>
              <TableCell>Időpont</TableCell>
              <TableCell>Művelet</TableCell>
              <TableCell>Típus</TableCell>
              <TableCell>Entitás név</TableCell>
              <TableCell>Felhasználó</TableCell>
              <TableCell>Forrás</TableCell>
              <TableCell width="60px">Részletek</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Nincs megjeleníthető audit log</Typography>
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <React.Fragment key={log.auditLogId}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleRowClick(log.auditLogId)}>
                        {expandedRow === log.auditLogId ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>{formatDate(log.timestamp)}</TableCell>
                    <TableCell>
                      <Chip label={log.action} color={getActionColor(log.action)} size="small" />
                    </TableCell>
                    <TableCell title={log.entityType}>{auditEntityTypeLabel(log.entityType)}</TableCell>
                    <TableCell>{log.entityName || '-'}</TableCell>
                    <TableCell>{log.userName || 'Rendszer'}</TableCell>
                    <TableCell>{renderSourceChip(log.clientSource)}</TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleShowDetails(log)}>
                        <InfoIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 0, borderBottom: expandedRow === log.auditLogId ? 1 : 0 }}>
                      <Collapse in={expandedRow === log.auditLogId} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, px: 2, bgcolor: 'grey.50' }}>
                          <Typography variant="subtitle2" gutterBottom>
                            További információk:
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Entity ID:</strong> {log.entityId || '-'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>IP cím:</strong> {log.ipAddress || '-'}
                          </Typography>
                          {log.additionalInfo && (
                            <Typography variant="body2" color="text.secondary">
                              <strong>További info:</strong> {log.additionalInfo}
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Sorok száma:"
        />
      </TableContainer>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Audit Log Részletek</DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Box>
              <Typography variant="body2" paragraph>
                <strong>Időpont:</strong> {formatDate(selectedLog.timestamp)}
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Művelet:</strong> <Chip label={selectedLog.action} color={getActionColor(selectedLog.action)} size="small" />
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Típus:</strong> {auditEntityTypeLabel(selectedLog.entityType)}
                {auditEntityTypeLabel(selectedLog.entityType) !== selectedLog.entityType ? ` (${selectedLog.entityType})` : ''}
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Entitás név:</strong> {selectedLog.entityName || '-'}
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Felhasználó:</strong> {selectedLog.userName || 'Rendszer'}
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Forrás:</strong> {getSourceLabel(selectedLog.clientSource)}
                {selectedLog.userAgent ? ` (${selectedLog.userAgent})` : ''}
              </Typography>

              {selectedLog.oldValues && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Régi értékek:
                  </Typography>
                  <Paper sx={{ p: 1, bgcolor: 'grey.100', maxHeight: 200, overflow: 'auto' }}>
                    <pre style={{ margin: 0, fontSize: '0.75rem' }}>
                      {typeof safeParseJSON(selectedLog.oldValues) === 'object'
                        ? JSON.stringify(safeParseJSON(selectedLog.oldValues), null, 2)
                        : selectedLog.oldValues}
                    </pre>
                  </Paper>
                </Box>
              )}

              {selectedLog.newValues && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Új értékek:
                  </Typography>
                  <Paper sx={{ p: 1, bgcolor: 'grey.100', maxHeight: 200, overflow: 'auto' }}>
                    <pre style={{ margin: 0, fontSize: '0.75rem' }}>
                      {typeof safeParseJSON(selectedLog.newValues) === 'object'
                        ? JSON.stringify(safeParseJSON(selectedLog.newValues), null, 2)
                        : selectedLog.newValues}
                    </pre>
                  </Paper>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Bezárás</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditLogViewer;
